import React, { ComponentProps, MutableRefObject, useCallback, useEffect, useRef } from 'react';

import cx from 'classnames';
import { isNil } from 'ramda';
import { CSSObjectWithLabel, GroupBase } from 'react-select-5';

import { openRoomPreview } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/Event/ModalContents/Edit/AccomodationModal/AccomodationForm/functions';
import { usePropertyPageQuery } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/Event/ModalContents/Edit/AccomodationModal/hooks';
import { IRoom } from 'components/shared/Assets/shared/Form/Document/types';
import { Select } from 'components/shared/Select';
import { Menu as DefaultMenu } from 'components/shared/Select/components/Menu';
import { CheckboxOption } from 'components/shared/Select/options/CheckboxOption';
import { GetRefT } from 'utils/types';

import s from './index.module.css';
import { useDnDActiveObserver } from './useDnDActiveObserver';

type Props = {
  id: number;
  type: string;
  editedInProposal: boolean;
  onDismiss: () => void;
  value: Partial<IRoom>[];
  onChange: (newRooms: Partial<IRoom>[]) => void;
  setPreviewOpen: (open: boolean) => void;
  previewOpen: boolean;
  selectRef: React.MutableRefObject<HTMLDivElement | null>;
};

export function RoomsDropdown(props: Props) {
  const pageQuery = usePropertyPageQuery(
    { pageId: props.id },
    { refetchOnReconnect: false, refetchOnWindowFocus: false, staleTime: Infinity },
  );

  const { previewOpen, setPreviewOpen } = props;

  const selectRef: MutableRefObject<GetRefT<
    NonNullable<
      ComponentProps<typeof Select<IRoom & { __selectAll__?: boolean; __isNew__?: boolean }, true>>['selectRef']
    >
  > | null> = useRef(null);

  const { isDnDActive } = useDnDActiveObserver({ ref: props.selectRef });

  const Menu = useCallback(
    (menuProps: ComponentProps<typeof DefaultMenu<IRoom, true>>) => {
      const menuContent = (() => {
        if (isDnDActive) return null;

        return (
          <>
            <div
              className={cx(s.header, { [s.longText]: props.editedInProposal })}
              onClick={(e) => e.stopPropagation()}
            >
              {props.type === 'property'
                ? props.editedInProposal
                  ? "Heads up! You can't change rooms because you've edited the page within the proposal. If you need to change rooms, please delete the whole page, click Save, and add it again with the correct rooms."
                  : 'Which room(s) would you like to add?'
                : 'You cannot add rooms to a non-property page'}
            </div>
            {props.type === 'property' && !props.editedInProposal ? menuProps.children : null}
          </>
        );
      })();

      return (
        <DefaultMenu<IRoom, true>
          hideFakeCreateOption
          hideHint={props.type !== 'property' || props.editedInProposal || isDnDActive}
          {...menuProps}
        >
          {menuContent}
        </DefaultMenu>
      );
    },
    [isDnDActive],
  );

  const value = pageQuery.data?.rooms?.filter(
    (optionRoom) =>
      props.value?.some((valueRoom) =>
        !isNil(valueRoom.id) || !isNil(optionRoom.id)
          ? optionRoom.id === valueRoom.id
          : optionRoom.key === valueRoom.key,
      ) || false,
  );

  useEffect(() => {
    setTimeout(() => selectRef.current?.focus());
  }, []);

  const openPagePreview = React.useCallback(
    async (pageId?: number) => {
      if (!pageId) return;
      if (!pageQuery.data) return;
      setPreviewOpen(true);

      const handledRooms = pageQuery.data?.rooms.map((room) => ({ ...room, label: room.name, value: room.id! })) || [];
      await openRoomPreview(pageId, { propertyPage: pageQuery.data, rooms: handledRooms });

      setPreviewOpen(false);
    },
    [pageQuery.data],
  );

  return (
    <Select
      options={pageQuery.data?.rooms}
      isLoading={pageQuery.isLoading}
      selectRef={selectRef}
      menuIsOpen
      lockMenu={previewOpen}
      isMulti
      blurInputOnSelect={false}
      hideSelectedOptions={false}
      onMenuClose={() => !previewOpen && props.onDismiss()}
      closeMenuOnSelect={false}
      value={value}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => String(option.id || option.key!)}
      onChange={(values) => props.onChange([...values])}
      styles={{
        control: () => selectStyles.control,
        menu: () => selectStyles.menu,
      }}
      components={{
        Option: (props) => {
          return (
            <CheckboxOption<
              IRoom & { __selectAll__?: boolean; __isNew__?: boolean },
              true,
              GroupBase<IRoom & { __selectAll__?: boolean; __isNew__?: boolean }>
            >
              {...props}
              hasPreview={true}
              onPreview={() => openPagePreview(props.data.id)}
            />
          );
        },
        Menu,
      }}
      classNames={{
        option: () => s.option,
      }}
    />
  );
}

const selectStyles: Record<string, CSSObjectWithLabel> = {
  control: {
    position: 'absolute',
    zIndex: -1,
    opacity: 0,
  },
  menu: {
    position: 'relative',
    width: '100%',
    top: 0,
  },
};
